<template>
    <div class="right-groups-manager">
        <div class="right-groups-header">
            <h4>User Permissions</h4>
            <!-- Add Permission Button -->
            <div v-if="!readonly" class="add-permission-button">
                <button type="button" class="btn btn-primary" @click="toggleAddForm" :class="{ active: showAddForm }">
                    {{ showAddForm ? "Cancel" : "Add Permission" }}
                </button>
            </div>
        </div>

        <!-- Add new permission form -->
        <div v-if="!readonly && showAddForm" class="add-permission-form">
            <div class="form-row">
                <div class="form-field">
                    <InputContainer label="Publisher" name="publisher">
                        <div class="base-input-container">
                            <LaBaseSelect
                                id="publisher-select"
                                :items="publisherOptions"
                                item-value="id"
                                item-text="name"
                                v-model="newPermission.publisher"
                                placeholder="Select publisher"
                            />
                        </div>
                    </InputContainer>
                </div>
                <div class="form-field">
                    <InputContainer label="Role" name="role">
                        <div class="base-input-container">
                            <LaBaseSelect
                                id="role-select"
                                :items="roleOptions"
                                v-model="newPermission.role_code"
                                placeholder="Select role"
                            />
                        </div>
                    </InputContainer>
                </div>
                <div class="form-field button-field">
                    <InputContainer label="" name="button-container">
                        <button
                            type="button"
                            class="btn btn-success"
                            @click="addRightGroup"
                            :disabled="!canAddPermission"
                        >
                            Save
                        </button>
                    </InputContainer>
                </div>
            </div>
        </div>

        <!-- Existing right groups -->
        <div v-if="rightGroups && rightGroups.length > 0" class="right-groups-list">
            <div class="right-group-item" v-for="group in rightGroups" :key="group.id">
                <div class="right-group-info">
                    <span class="publisher-name">{{ group.publisher }}</span>
                    <span class="role-badge" :class="getRoleBadgeClass(group.role_code)">
                        {{ group.role_code }}
                    </span>
                    <span class="role-badge role-default" v-if="group.publisher_is_yield !== null">
                        {{ group.publisher_is_yield ? "Yield" : "AAB" }}
                    </span>
                </div>
                <div class="right-group-actions" v-if="!readonly">
                    <LaBaseIcon
                        name="delete"
                        class="remove-icon"
                        @click="removeRightGroup(group)"
                        title="Remove permission"
                    />
                </div>
            </div>
        </div>

        <!-- No permissions message -->
        <div v-else class="no-permissions">
            <span>No permissions assigned</span>
        </div>
    </div>
</template>

<script>
import { computed, ref } from "vue";
import LaBaseSelect from "@/components/inputs/BaseSelect.vue";
import InputContainer from "@/components/inputs/InputContainer.vue";
import LaBaseIcon from "@/components/BaseIcon.vue";
import store from "@/store/store";
import { getterTypes as publisherGetterTypes } from "@/store/Publisher/types";

export default {
    name: "RightGroupsManager",
    components: {
        LaBaseSelect,
        InputContainer,
        LaBaseIcon,
    },
    props: {
        userId: {
            type: [Number, String],
            required: true,
        },
        rightGroups: {
            type: Array,
            default: () => [],
        },
        readonly: {
            type: Boolean,
            default: false,
        },
    },
    emits: ["add-right-group", "remove-right-group", "update-right-groups"],
    setup(props, { emit }) {
        const newPermission = ref({
            publisher: null,
            role_code: null,
        });

        const roleOptions = ref(["account", "admin", "super_admin"]);
        const showAddForm = ref(false);

        // Get publishers from store
        const publishers = computed(() => store.getters[publisherGetterTypes.getPublishers] || []);

        const publisherOptions = computed(() => {
            return publishers.value.map((pub) => ({
                id: pub.id,
                name: pub.name || pub.company_name || `Publisher ${pub.id}`,
            }));
        });

        const canAddPermission = computed(() => {
            if (!newPermission.value.publisher || !newPermission.value.role_code) {
                return false;
            }

            // Check if this combination already exists
            const exists = props.rightGroups.some(
                (group) =>
                    group.publisher_id === newPermission.value.publisher.id &&
                    group.role_code === newPermission.value.role_code
            );

            return !exists;
        });

        const getRoleBadgeClass = (roleCode) => {
            const classMap = {
                super_admin: "role-super-admin",
                admin: "role-admin",
                account: "role-account",
            };
            return classMap[roleCode] || "role-default";
        };

        const toggleAddForm = () => {
            showAddForm.value = !showAddForm.value;
            // Reset form when closing
            if (!showAddForm.value) {
                newPermission.value = {
                    publisher: null,
                    role_code: null,
                };
            }
        };

        const addRightGroup = () => {
            if (!canAddPermission.value) return;

            const newRightGroup = {
                user_id: props.userId,
                publisher_id: newPermission.value.publisher.id,
                role_code: newPermission.value.role_code,
                publisher: newPermission.value.publisher.name,
            };

            emit("add-right-group", newRightGroup);

            // Reset form and close it
            newPermission.value = {
                publisher: null,
                role_code: null,
            };
            showAddForm.value = false;
        };

        const removeRightGroup = (group) => {
            if (window.confirm(`Remove permission "${group.publisher} - ${group.role_code}"?`)) {
                emit("remove-right-group", group);
            }
        };

        // Load publishers when component mounts
        if (!publishers.value || publishers.value.length === 0) {
            const { actionTypes: publisherActionTypes } = require("@/store/Publisher/types");
            store.dispatch(publisherActionTypes.getData);
        }

        return {
            newPermission,
            roleOptions,
            publisherOptions,
            canAddPermission,
            showAddForm,
            getRoleBadgeClass,
            toggleAddForm,
            addRightGroup,
            removeRightGroup,
        };
    },
};
</script>

<style lang="scss" scoped>
:deep(.form-field .input-label) {
    min-width: 3rem;
    padding-right: 1rem;
}

.right-groups-manager {
    max-width: 100%;
    width: 100%;

    .right-groups-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 1rem;

        h4 {
            margin: 0;
            color: $black;
            font-size: 1.1rem;
        }

        .add-permission-button {
            .btn {
                padding: 0.5rem 1rem;
                border: none;
                border-radius: $normal-radius;
                cursor: pointer;
                font-size: 0.9rem;
                transition: all 0.3s ease;

                &.btn-primary {
                    background-color: $green-2;
                    color: white;

                    &:hover {
                        background-color: darken($green-2, 10%);
                    }

                    &.active {
                        background-color: #e74c3c;

                        &:hover {
                            background-color: darken(#e74c3c, 10%);
                        }
                    }
                }
            }
        }
    }

    .right-groups-list {
        margin-bottom: 1.5rem;
        max-height: 20rem;
        overflow-y: auto;
        padding-right: 0.25rem;

        .right-group-item {
            display: flex;
            justify-content: space-between;
            align-items: center;
            padding: 0.75rem;
            margin-bottom: 0.5rem;
            border: 1px solid $dark-grey;
            border-radius: $normal-radius;
            background-color: $white;

            .right-group-info {
                display: flex;
                align-items: center;
                gap: 1rem;

                .publisher-name {
                    font-weight: 500;
                    color: $blue;
                }

                .role-badge {
                    padding: 0.25rem 0.5rem;
                    border-radius: 4px;
                    font-size: 0.8rem;
                    font-weight: 500;
                    text-transform: uppercase;

                    &.role-super-admin {
                        background-color: #ff6b6b;
                        color: white;
                    }

                    &.role-admin {
                        background-color: #4ecdc4;
                        color: white;
                    }

                    &.role-account {
                        background-color: #95a5a6;
                        color: white;
                    }

                    &.role-default {
                        background-color: $dark-grey;
                        color: $light-grey;
                    }
                }
            }

            .right-group-actions {
                .remove-icon {
                    cursor: pointer;
                    color: #e74c3c;
                    font-size: 1.2rem;

                    &:hover {
                        color: #c0392b;
                    }
                }
            }
        }
    }

    .no-permissions {
        padding: 1rem;
        text-align: center;
        color: $light-grey;
        font-style: italic;
        border: 1px dashed $dark-grey;
        border-radius: $normal-radius;
        margin-bottom: 1.5rem;
    }

    .add-permission-form {
        margin-bottom: 1.5rem;
        padding: 1rem;
        border: 1px solid $dark-grey;
        border-radius: $normal-radius;
        background-color: #f8f9fa;

        .form-row {
            display: flex;
            align-items: flex-end;
            gap: 1rem;

            .form-field {
                flex: 1;

                &.button-field {
                    flex: 0 0 auto;

                    .btn {
                        padding: 0.7rem 1rem;
                        border: none;
                        border-radius: $normal-radius;
                        cursor: pointer;
                        font-size: 1rem;
                        white-space: nowrap;

                        &.btn-success {
                            background-color: $green;
                            color: white;

                            &:hover:not(:disabled) {
                                background-color: darken($green, 10%);
                            }

                            &:disabled {
                                background-color: $dark-grey;
                                cursor: not-allowed;
                            }
                        }
                    }
                }
            }

            :deep(.base-input-container) {
                .select-trigger {
                    width: 100%;
                    min-width: auto;
                    max-width: none;
                }
            }

            :deep(.input-container) {
                margin-bottom: 0 !important;
            }

            :deep(.input-container-error) {
                min-height: 0 !important;
            }
        }
    }
}

// Dark mode support
body.dark {
    .right-groups-manager {
        .right-groups-header {
            h4 {
                color: $dark-text;
            }

            .add-permission-button {
                .btn.btn-primary {
                    background-color: $green-2;

                    &:hover {
                        background-color: lighten($green-2, 10%);
                    }

                    &.active {
                        background-color: #e74c3c;

                        &:hover {
                            background-color: lighten(#e74c3c, 10%);
                        }
                    }
                }
            }
        }

        .add-permission-form {
            background-color: $bt-light;
            border-color: $bt-light;

            .form-row {
                display: flex;
                align-items: flex-end;
                gap: 1rem;

                .form-field {
                    flex: 1;

                    &.button-field {
                        flex: 0 0 auto;

                        .btn.btn-success {
                            background-color: $green;

                            &:hover:not(:disabled) {
                                background-color: lighten($green, 10%);
                            }
                        }
                    }
                }

                :deep(.base-input-container) {
                    .select-trigger {
                        width: 100%;
                        min-width: auto;
                        max-width: none;
                    }
                }

                :deep(.input-container) {
                    margin-bottom: 0 !important;
                }

                :deep(.input-container-error) {
                    min-height: 0 !important;
                }
            }
        }

        .right-groups-section {
            background-color: $bt-light !important;
            border-color: $bt-light !important;
            border-top-color: $bt-light !important;
            border-top: 1px solid $bt-light !important;
        }

        :deep(.right-groups-section) {
            background-color: $bt-light !important;
            border-color: $bt-light !important;
            border-top-color: $bt-light !important;
            border-top: 1px solid $bt-light !important;
        }

        .right-groups-list {
            border-color: $gray-600;
            scrollbar-color: $background-light $light-grey;

            &::-webkit-scrollbar-thumb {
                background: $background-light;
            }

            .right-group-item {
                //background-color: $card-background-light;
                background-color: $bt-light;
                border-color: $bt-light;

                .right-group-info {
                    .publisher-name {
                        color: $dark-text;
                    }
                }
            }
        }

        .no-permissions {
            border-color: $card-background-light;
            color: $text;
        }
    }
}

body.variant-performax {
    .right-groups-manager {
        .right-groups-section {
            background-color: $white !important;
            border-color: rgba($px-purple, 0.2) !important;
        }

        :deep(.right-groups-section) {
            background-color: $white !important;
            border-color: rgba($px-purple, 0.2) !important;
        }

        .right-groups-list {
            background-color: $white;

            .right-group-item {
                background-color: $white;
                border-color: rgba($px-purple, 0.2);
            }
        }

        .add-permission-form {
            background-color: $white;
            border-color: rgba($px-purple, 0.2);
        }
    }
}
</style>
